import React from 'react';
import LinkComp from '../LinkComp';

// HTML rebuild of the old training-hero.png. Every circle is a plain link
// (add/move/remove entries below); titles and labels are plain text. Only the
// mountain silhouette and the per-station icons are images, extracted from the
// original artwork into /img/continuum/. All styling lives in BrokerageMap.scss.

// Circle centers in % of the map box (left/top). The circles sit evenly spaced
// on the same ellipse arc the original artwork used (center 50%/110%, radii
// 47.3%/76.2%) — recompute the spacing on that arc if the count changes again.
const POSITIONS = [
    { x: 5.2,  y: 85.5 }, // 1
    { x: 10.3, y: 68.5 }, // 2
    { x: 17.9, y: 54.0 }, // 3
    { x: 27.4, y: 43.0 }, // 4
    { x: 38.4, y: 36.1 }, // 5
    { x: 50.0, y: 33.8 }, // 6
    { x: 61.6, y: 36.1 }, // 7
    { x: 72.6, y: 43.0 }, // 8
    { x: 82.1, y: 54.0 }, // 9
    { x: 89.7, y: 68.5 }, // 10
    { x: 94.8, y: 85.5 }, // 11
];

// Fixed fallback stations (slug + name + position index) mirroring the TRAINING
// table's brokerage-continuum chapters. Used when no `chapters` data is supplied
// so the map links still render (e.g. before TRAINING data loads). When real
// chapter data is passed (ChapterPage / production), that data is used instead.
const STATIONS = [
    { id: 'research-and-database',     name: 'Research & Database',     index: 1 },
    { id: 'cold-call',                 name: 'Calling',                 index: 2 },
    { id: '1st-meeting',               name: '1st Meeting',             index: 3 },
    { id: 'underwriting-and-proposal', name: 'Underwriting & Proposal', index: 4 },
    { id: '2nd-meeting',               name: '2nd Meeting',             index: 5 },
    { id: 'marketing',                 name: 'Marketing',               index: 6 },
    { id: 'fielding-offers',           name: 'Fielding Offers',         index: 7 },
    { id: 'contract',                  name: 'Contract',                index: 8 },
    { id: 'escrow',                    name: 'Escrow',                  index: 9 },
    { id: 'close',                     name: 'Close',                   index: 10 },
    { id: 'post-close',                name: 'Post Close',              index: 11 },
];

export default function BrokerageMap({
    chapters = [],
    activeSlug = null,
    titleTop = 'SUMMIT RE',
    titleMain = 'BROKERAGE CONTINUUM',
}) {
    const items = (chapters && chapters.length) ? chapters : STATIONS;

    // Dashed trail: corner stub → every circle center → corner stub. Each leg is
    // a thin rotated div. Angles are computed on the artwork's 1000x600 grid so
    // they stay correct at any size (the canvas keeps that aspect ratio).
    const trail = [[1.5, 98.7]]
        .concat(POSITIONS.map(p => [p.x, p.y]))
        .concat([[99.0, 99.0]]);
    const dashes = trail.slice(0, -1).map(([x1, y1], i) => {
        const [x2, y2] = trail[i + 1];
        const dx = (x2 - x1) * 10;
        const dy = (y2 - y1) * 6;
        return {
            left: `${x1}%`,
            top: `${y1}%`,
            width: `${Math.hypot(dx, dy) / 10}%`,
            transform: `translateY(-50%) rotate(${Math.atan2(dy, dx) * 180 / Math.PI}deg)`,
        };
    });

    return (
        <div className='BrokerageMap'>
            <div className='brokerage-map__canvas'>
                <div className='brokerage-map__title brokerage-map__title--top'>{titleTop}</div>
                <div className='brokerage-map__title brokerage-map__title--main'>{titleMain}</div>

                <img className='brokerage-map__mountain' src='/img/continuum/mountain.png' alt='' />

                {dashes.map((style, i) => (
                    <div key={i} className='brokerage-map__dash' style={style} />
                ))}

                {items.map(chapter => {
                    const pos = POSITIONS[chapter.index - 1];
                    if (!pos) return null;
                    const isActive = activeSlug === chapter.id;
                    return (
                        <LinkComp
                            key={chapter.id}
                            to={`${arc.path.base}training/brokerage-continuum/${chapter.id}`}
                            className={`brokerage-map__station${isActive ? ' brokerage-map__station--active' : ''}`}
                            style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                            title={chapter.name}
                        >
                            <img
                                className='brokerage-map__icon'
                                src={`/img/continuum/icon-${chapter.id}.png`}
                                alt=''
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                            <span className='brokerage-map__label'>{chapter.name}</span>
                        </LinkComp>
                    );
                })}
            </div>
        </div>
    );
}
